<template>
    <b-modal
        :ref="modalRef"
        title-tag="h2"
        :title="`Approve ` + (estimate.name || `Estimate #` + estimate.index)"
        lazy
    >
        <form id="approve-form" ref="approve-form">
            <div v-if="isPatientRequired">
                <p>
                    Clicking on the Assign button will assign the selected
                    patient to the items with no patient. The Approve dialog
                    will be displayed after patients are assigned.
                </p>

                <b-form-group
                    label-for="patient"
                    label="Patient:"
                    label-cols="3"
                >
                    <vet-select
                        id="patient"
                        ref="patient"
                        v-model="selectedPatient"
                        :client-id="client ? client.id : null"
                        :show-menu="false"
                        module="client-patient"
                        placeholder="Patient Name (required)"
                        label="name"
                        name="patient"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('patient') }"
                        :status="[1, 3]"
                        :selectable="selectablePatient"
                        :showDisabledTooltip="true"
                        @isLoading="isLoadings"
                        @options-loaded="checkItemRuleAllPatients"
                        class="vs-remove-highlight"
                    >
                    </vet-select>
                </b-form-group>
                <b-form-group :label-cols="3" class="pb-2">
                    <apply-item-rule-checkbox v-model="isCheckItemRule" />
                </b-form-group>
            </div>
            <div id="approve-form" v-show="showApproveForm">
                <b-form-group label-for="client" label="Client:" label-cols="4">
                    <vet-select
                        id="client"
                        v-model="selectedClient"
                        :show-menu="false"
                        name="client"
                        module="client"
                        v-validate="'required'"
                        placeholder="Client name (required)"
                        :class="{ 'border-danger': errors.has('client') }"
                    />
                </b-form-group>
                <b-form-group
                    v-if="showLocationSelect"
                    label-for="location"
                    label="Location:"
                    label-cols="4"
                >
                    <select
                        id="location"
                        v-model="selectedLocation"
                        class="form-control"
                    >
                        <option
                            :value="null"
                            v-if="
                                $root.setting.location ==
                                CSETTINGS.SERVICE_LOCATION.BOTH
                            "
                            >In Clinic</option
                        >
                        <option
                            :value="null"
                            v-if="
                                selectedClient &&
                                $root.setting.location ==
                                    CSETTINGS.SERVICE_LOCATION.MOBILE
                            "
                            >Select location (optional)</option
                        >
                        <option
                            v-for="location in locations"
                            :key="location.id"
                            :value="location"
                            >{{ location.name }}{{ location.address }}</option
                        >
                    </select>
                </b-form-group>
                <b-form-group label-for="date" label="Date:" label-cols="4">
                    <date-picker
                        :config="datePickerOptions"
                        v-model="selectedDate"
                        v-validate="'required'"
                        class="form-control"
                        placeholder="Create Date (required)"
                        :class="{ 'border-danger': errors.has('date') }"
                        name="date"
                    ></date-picker>
                </b-form-group>
                <b-form-group
                    label-for="signature"
                    label="Client Signature:"
                    label-cols="12"
                >
                    <div id="signature-section">
                        <div class="row-fluid">
                            <ul
                                class="sigNav nav nav-tabs subnavbar mb-2 signature-options"
                            >
                                <li class="drawIt" :class="{ active: isDraw }">
                                    <strong
                                        @click="setDraw()"
                                        class="cursor-pointer"
                                    >
                                        <i class="cus-pencil"></i>Draw It
                                    </strong>
                                </li>
                                <li class="typeIt" :class="{ active: !isDraw }">
                                    <strong
                                        @click="setType()"
                                        class="cursor-pointer"
                                    >
                                        <i class="cus-keyboard"></i>Type It
                                    </strong>
                                </li>
                            </ul>
                            <div class="draw-panel" v-if="isDraw">
                                <input
                                    id="signature_output"
                                    type="hidden"
                                    name="signature_output"
                                    class="output required"
                                />
                            </div>
                            <div class="type-panel mb-5" v-show="!isDraw">
                                <b-form-group
                                    label-for="signature_name"
                                    label="Name:"
                                    label-cols="4"
                                >
                                    <b-form-input
                                        v-model="signatureName"
                                        id="signature_name"
                                        name="signature_name"
                                        class="form-control name col-12"
                                    ></b-form-input>
                                </b-form-group>
                                <div class="typed" v-show="!isDraw"></div>
                            </div>
                            <canvas
                                @click="onDraw()"
                                class="pad"
                                :class="{ hide: !isDraw }"
                                width="450"
                                height="150"
                            ></canvas>
                        </div>
                    </div>
                </b-form-group>
            </div>
        </form>
        <template v-slot:modal-footer>
            <modal-footer
                v-if="!isPatientRequired"
                :is-loading="isLoading"
                ok-title="Approve"
                @ok-action="approve"
                @cancel-action="close"
            />
            <modal-footer
                v-else
                :is-loading="isLoading"
                ok-title="Assign"
                @ok-action="assign"
                @cancel-action="close"
            />
        </template>
    </b-modal>
</template>
<style type="text/css">
.signature-options li {
    padding: 3px 10px;
    margin-bottom: -1px;
}

.signature-options li.active {
    border: 1px solid #ccc;
    border-bottom: 1px solid white;
    border-radius: 3px 3px 0 0;
}

.pad.hide {
    border: 0px;
    height: 10px;
}
</style>
<script type="text/javascript">
/**
 * @see apps/application/controllers/billing/estimate.php @confirm_approve() & @approve()
 * @see apps/application/views/staff/billing/invoice/estimate/approve.php
 * @see apps/assets/js/module/billing/estimate.js @confirm_approve() & @approve()
 */
import { filter, isEmpty, isUndefined, isNull, includes } from "lodash";
import { DateTime } from "luxon";
import Client from "@/services/client";
import CSETTINGS from "@/constants/settings";
import Billings from "@/services/billing/billing-estimate";
import ItemRulesServices from "@/services/items/items-rule";
import ItemRulesHelper from "@/mixins/item-rules";
import ApplyItemRuleCheckbox from "@/components/commons/apply-item-rule-checkbox.vue";

import "@/jquery/signature/jquery.signaturepad.js";
import "@/css/signature/jquery.signaturepad.css";

export default {
    components: {
        vetSelect: () => import("@/module/commons/vet-select.vue"),
        ModalFooter: () => import("@/components/elements/modal-footer"),
        ApplyItemRuleCheckbox
    },
    mixins: [ItemRulesHelper],
    props: {
        modalRef: {
            type: String,
            required: true
        },
        estimate: {
            type: Object,
            default() {
                return {};
            }
        },
        client: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {
            CSETTINGS,
            locations: [],
            selectedDate: null,
            selectedClient: null,
            selectedLocation: null,
            datePickerOptions: {
                useCurrent: true,
                format: "DD/MM/YYYY"
            },
            isDraw: true,
            signatureOutput: null,
            signatureName: null,
            isLoading: false,
            selectedPatient: null,
            details: null,
            isPatientRequired: false,
            showApproveForm: true,
            isCheckItemRule: true
        };
    },
    computed: {
        isOkDisabled() {
            return this.selectedDate == null;
        },
        showLocationSelect() {
            return (
                this.$root.setting &&
                includes(
                    [
                        CSETTINGS.SERVICE_LOCATION.MOBILE,
                        CSETTINGS.SERVICE_LOCATION.BOTH
                    ],
                    this.$root.setting.location
                )
            );
        }
    },
    watch: {
        selectedClient: {
            handler(newData, oldData) {
                let self = this;

                if (newData) {
                    this.getLocations(newData.id);
                } else {
                    self.selectedLocation = null;
                    self.locations = [];
                }
            }
        },
        isCheckItemRule(value) {
            if (value) {
                this.checkItemRuleAllPatients();
            }
        }
    },
    methods: {
        selectablePatient(opt) {
            let rulePassed = true;
            if (this.isCheckItemRule && opt.rules) {
                const failedRules = Object.values(opt.rules).filter(
                    rule => !rule.isPassed
                );
                rulePassed = failedRules.length === 0;
                opt.disabled = !rulePassed;

                if (opt.disabled) {
                    opt.disabledMessage = this.MESSAGE_PATIENT_DISABLED;
                }
            }

            if (rulePassed) {
                opt.disabled = false;
                opt.disabledMessage = "";
            }

            return rulePassed;
        },
        async open() {
            let self = this;
            self.details = await Billings.getEstimate(this.estimate.id, 0);
            self.isPatientRequired =
                self.details?.resource?.patient_required && self.client;

            if (self.isPatientRequired) {
                this.showApproveForm = false;
            }
            if (!isEmpty(this.client)) {
                this.getLocations(this.client.id);
                this.setSelectedClient(this.client);
            }
            this.setSelectedDate();
            this.$refs[this.modalRef].show();
            this.$nextTick(() => {
                this.initSignaturePad();
            });
            this.datePickerOptions.format = this.getDateFormat();
        },
        async checkItemRuleAllPatients() {
            const iitemsWithNoPatient = filter(
                this.details.resource.active_estimate_items,
                item => !item.patient_id
            );
            const patientOptions = this.$refs.patient.sortOptions;

            iitemsWithNoPatient.map(async iitem => {
                const item = iitem.item;
                const res = await ItemRulesServices.getRules(item.id);
                item.rules = res.resources;
                patientOptions.map(async patient => {
                    patient.rules = patient.rules || {};
                    patient.rules[item.id] = {
                        isPassed: true,
                        message: ""
                    };
                    await this.checkItemRulePatient(patient, item);
                });
            });
        },
        async checkItemRulePatient(patient, item) {
            let result = {
                isPassed: true,
                message: ""
            };
            if (item) {
                if (this.isCheckItemRule) {
                    const res = await this.checkRuleItem(patient, item);
                    result.isPassed = res.isPassed;
                    result.message = res.message;
                }

                patient.rules = patient.rules || {};
                patient.rules[item.id] = result;
            }
            if (
                !result.isPassed &&
                this.selectedPatient &&
                this.selectedPatient.id === patient.id
            ) {
                this.selectedPatient = null;
            }
        },
        setDraw() {
            this.isDraw = true;
            this.signatureName = "";
        },
        setType() {
            this.isDraw = false;
            this.signatureOutput = "";
            $("#approve-form").signaturePad().clearCanvas();
        },
        initSignaturePad() {
            let self = this;
            $("#approve-form").signaturePad({
                defaultAction: "drawIt",
                drawOnly: false,
                clear: $(".clearButton"),
                lineWidth: 0,
                lineTop: 150,
                validateFields: true,
                penColour: "#00F",
                penWidth: 4
            });
        },
        onDraw() {
            this.signatureOutput = $("#signature_output").val();
        },
        setSelectedDate() {
            this.selectedDate = this._now().toFormat(this._dateFormat);
        },
        setSelectedClient(client) {
            this.selectedClient = {
                id: client.id,
                label: client.firstname + " " + client.lastname
            };
        },
        getLocations(clientId) {
            Client.getLocations(clientId).then(response => {
                this.locations = response.resources;
                this.setSelectedLocation(response.resources);
            });
        },
        setSelectedLocation(locations) {
            let location =
                this.estimate.estimate_location || this.estimate.location;
            if (location) {
                locations.forEach(element => {
                    let selLocationId = location.location_id || location.id;
                    if (selLocationId == element.id) {
                        this.selectedLocation = element;
                    }
                });
            }
        },
        async approve() {
            let valid = await this.$validator.validateAll();
            if (!valid || this.isOkDisabled) {
                return false;
            }

            this.isLoading = true;

            let estimate = this.estimate;
            let location = this.selectedLocation;
            let signatureOutput = $("#signature_output").val();

            let params = {
                estimate_id: estimate.id,
                client_id: this.selectedClient.id,
                start_timestamp: Math.round(this._now().toSeconds()),
                business_location: this.$root.setting.location,
                client: this.selectedClient.label,
                location_id: location
                    ? "[" + location.type + "]" + location.id
                    : "",
                start: this.selectedDate,
                signature:
                    !isUndefined(signatureOutput) && !isNull(signatureOutput)
                        ? signatureOutput
                        : "",
                name: this.signatureName ? this.signatureName : ""
            };

            Billings.approveEstimate(params)
                .then(response => {
                    if (response.state == 0) {
                        let msg =
                            response.message.code == 200
                                ? "Estimate approved"
                                : response.message;

                        this._message(msg);
                        this.$emit("reload");
                    } else {
                        this._message(response.message, "danger");
                    }
                    this.close();
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to approve estimate",
                        "danger"
                    );
                    console.error(error);
                    this.close();
                });
        },
        async assign() {
            let valid = await this.$validator.validateAll();
            if (!valid || this.isOkDisabled) {
                return false;
            }

            this.isLoading = true;
            let params = {
                patient_id: this.selectedPatient?.id
            };

            Billings.insertBulkPatient(params, this.estimate.id)
                .then(response => {
                    if (response.message.code == 200) {
                        let msg =
                            response.message.code == 200
                                ? "Successfully assigned patient"
                                : response.message;

                        this._message(msg);
                        this.isLoading = false;
                        this.isPatientRequired = 0;
                        this.showApproveForm = true;
                        this.$nextTick(() => {
                            this.initSignaturePad();
                        });
                        this.$emit("reload", "tabItems");
                    } else {
                        this._message(response.message, "danger");
                    }
                    this.isLoading = false;
                })
                .catch(error => {
                    this._message("Error : Unable to assign patient", "danger");
                    console.error(error);
                    this.isLoading = false;
                });
        },
        close() {
            this.isLoading = false;
            this.$refs[this.modalRef].hide();
        },
        isLoadings(isLoading) {
            this.isLoading = isLoading;
        }
    }
};
</script>
